import axios from 'axios';
import env from '../config/environment';
import { saveState, loadState } from './localStorage';

const getSplitValue = (key, raw, delim1, delim2) => {
  const vars = raw.split(delim1);

  for (let i = 0; i < vars.length; i += 1) {
    const pair = vars[i].split(delim2);
    if (pair.length === 2) {
      const pairKey = pair[0].trim();
      const pairValue = pair[1].trim();
      if (pairKey === key && pairValue) {
        return pairValue;
      }
    }
  }

  return null;
};

export const getQueryValue = (queryString, key) => getSplitValue(key, queryString.substring(1), '&', '=');

const devCacheLocally = process.env.DEV_CACHE_LOCALLY === 'true' && process.env.NODE_ENV === 'development';

class Api {
  constructor() {
    if (env.IsPWTest() === false) {
      this.proxy = axios.create({
        baseURL: env ? env.ApiBaseURL() : '/api',
        timeout: env.IsTest() ? 10 : 91000,
        withCredentials: true,
      });
    } else {
      this.proxy = {
        get: () => null,
        post: () => null,
        interceptors: {
          response: {
            use: () => null,
          },
        },
      };
    }

    if (devCacheLocally) { this.csrfToken = loadState('crsf') ?? undefined; }

    this.proxy.interceptors.response.use(this.handleResponseSuccess, this.handleResponseError);
  }

  handleResponseSuccess = (response) => {
    // On a successful /status call, stash the CSRF token for later use.
    if (response.config.url.endsWith(env.EndpointStatus()) || response.config.url.endsWith(env.EndpointIdentityStatus())) {
      this.csrfToken = response.headers['x-csrf-token'];

      if (devCacheLocally) { saveState('crsf', this.csrfToken); }
    }
    return response;
  };

  handleResponseError = (error) => {
    console.warn(`API request failed: ${error.message}`);
    return Promise.reject(error);
  };

  information() {
    return this.proxy.get('/');
  }

  get(endpoint) {
    return this.proxy.get(endpoint);
  }

  getblob(endpoint) {
    return this.proxy.get(endpoint, {
      responseType: 'blob',
    });
  }

  post(endpoint, params = {}, ignoreCSRF = false) {
    const { csrfToken } = this;
    const headers = {};
    if (!ignoreCSRF && !csrfToken) {
      console.error('Attempting to make a POST without a CSRF Token set.', endpoint);
    } else {
      headers['X-CSRF-Token'] = csrfToken;
    }

    return this.proxy.post(endpoint, params, { headers });
  }

  /** AUTH */
  saml() {
    return this.get(env.EndpointSaml());
  }

  saml2() {
    return this.get(env.EndpointSaml2());
  }

  samlSLO() {
    return this.get(env.EndpointSamlSLO());
  }

  samlLogout = () => this.post(env.EndpointSamlLogout(), {}, true);

  login = (username, password) => this.post(env.EndpointBasicAuthentication(), { username, password }, true);

  logout = () => this.post(env.EndpointLogout(), {}, true);

  refresh = (token) => this.post(env.EndpointRefresh(), { token }, true);

  /** FORM */
  save = (payload) => this.post(env.EndpointSave(), payload);

  checkStatus = () => this.get(env.EndpointCheckStatus());

  identityStatus = () => this.get(env.EndpointIdentityStatus());

  setAccount = (payload) => this.post(env.EndpointSetAccount(), payload);

  status = () => this.get(env.EndpointStatus());

  environment = () => this.get(env.EndpointEnvironment());

  apiVersion = () => this.get(env.EndpointApiVersion());

  getDraftPdf() {
    return this.getblob(env.EndpointDraftPdf());
  }

  submit() {
    return this.post(env.EndpointSubmit());
  }

  form = () => this.get(env.EndpointForm());

  /**
 *
 * @param {object} payload
 * @returns
 */
  validate(payload) {
    return this.post(env.EndpointValidate(), payload);
  }

  /**
 *
 * @param {object} payload
 * @returns
 */
  validateLog(payload) {
    return this.post(env.EndpointValidateLog(), payload);
  }

  /** ATTACHMENTS */
  listAttachments() {
    return this.get(env.EndpointAttachment());
  }

  saveAttachment(formData) {
    return this.post(env.EndpointAttachment(), formData);
  }

  updateAttachment(id, description) {
    return this.post(env.EndpointAttachmentUpdate(id), {
      description,
    });
  }

  getAttachment(id) {
    return this.getblob(env.EndpointAttachmentGet(id));
  }

  deleteAttachment(id) {
    return this.post(env.EndpointAttachmentDelete(id));
  }

  getCompleteForm() {
    return this.getblob(env.EndpointGetForm());
  }

  getPDFStatus() {
    return this.get(env.EndpointGetPDFStatus());
  }

  getServerTime() {
    return this.get(env.EndpointGetServerTime());
  }

  getAnnouncements = () => {
    const response = this.get(env.EndpointGetAnnouncements());
    return response;
  };

  /**
   * getInstituteSearch takes a education location object with searchtext and posts it to get a list of institutions
   * @param {object} payload
   * @returns
   */
  getInstituteSearch = (payload) => this.post(env.EndpointGetInstituteSearch(), payload);

  submitIdentityVerification = (payload) => this.post(env.EndpointIdentityVerification(), payload);
}

const api = new Api();

export { api };
